import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    Body1,
    Button,
    Heading1,
    Heading2,
    HorizontalDivider,
    Stack,
    useDisableBackButton,
} from '@myphx-engineering/phx-design-system';

import { LocationInfo } from '../../../components/common/LocationInfo';
import { Prescriber } from '../../../components/prescriber/Prescriber';
import { getFragment } from '../../../graphql';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    RenewalStatus as RenewalStatuses,
} from '../../../graphql/generated/graphql';
import { getMedicationDescription } from '../../../util/cabinet/prescriber-orders';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';

type RenewalConfig = {
    header: string;
    description1: string;
    description2: string;
};

const getRenewalConfig = (args: {
    status: RenewalStatuses;
    t: TFunction;
    productDescription?: string | null;
}): RenewalConfig | null => {
    const { status, t, productDescription } = args;

    switch (status) {
        case RenewalStatuses.Denied: {
            return {
                header: t('renewalStatus.denied.header'),
                description1: t('renewalStatus.denied.description1', {
                    medication: productDescription,
                }),
                description2: t('renewalStatus.denied.description2'),
            };
        }
        default:
            return null;
    }
};

export const RenewalStatus = ({
    prescriberOrder,
}: {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
}) => {
    const { t } = useTranslation();
    useDisableBackButton(true);
    const renewalDetails = prescriberOrder.renewalDetails;

    if (!renewalDetails) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const prescriber = getFragment(prescriberOrder.prescriber);
    const offer = getFragment(prescriberOrder.latestSelectedOffer);
    const provider = offer ? getFragment(offer.pharmacyProvider) : null;

    if (!provider) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const productDescription = getMedicationDescription(prescriberOrder);
    const config = getRenewalConfig({
        status: renewalDetails.status,
        t,
        productDescription,
    });

    if (!config) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const { header, description1, description2 } = config;

    const handleCallProvider = () => {
        if (prescriber?.phoneNumber) {
            window.location.href = `tel:${prescriber.phoneNumber}`;
        }
    };

    return (
        <Stack>
            <Heading1 ta="center">{header}</Heading1>

            <Stack gap="sm">
                <Body1>{description1}</Body1>
                <Body1>{description2}</Body1>
            </Stack>

            {renewalDetails.status === RenewalStatuses.Denied &&
                prescriber?.phoneNumber && (
                    <Button
                        onClick={handleCallProvider}
                        color="error"
                        fullWidth
                    >
                        {t('prescription.cancelled.modal.callProvider')}
                    </Button>
                )}

            <HorizontalDivider variant="full" />

            <Stack gap="xxl">
                <Stack gap="sm">
                    <Heading2>
                        {t('orderConfirmation.pharmacy.subHeader')}
                    </Heading2>
                    <LocationInfo location={provider} />
                </Stack>

                <Prescriber prescriber={prescriber} />
            </Stack>
        </Stack>
    );
};
